import { useState } from "react";
import BaseButton from "../../../components/ui/BaseButton";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import AddGeneralInsuranceModal from "../components/AddGeneralInsuranceModal";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { deleteUserQualification, fetchUserQualificationsByType } from "../../../services/api/user";

const Insurance = ({ userId, insuranceId }) => {
  const dispatch = useDispatch();
  const [isGeneralInsuranceOpen, setIsGeneralInsuranceOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [insuranceToDelete, setInsuranceToDelete] = useState(null);

  const { data: insurances, refetch: refetchUserQualificationsByType } = useQuery({
    queryKey: ["insurances"],
    queryFn: () => fetchUserQualificationsByType(userId, insuranceId),
    enabled: !!userId && !!insuranceId,
  });

  const { isPending: isDeleting, mutateAsync: deleteInsuranceMutation } = useMutation({
    mutationFn: deleteUserQualification,
    enabled: false,
  });

  const handleUploadClick = () => {
    setSelectedInsurance(null);
    setIsGeneralInsuranceOpen(true);
  };

  const handleEditInsurance = (insurance) => {
    setSelectedInsurance(insurance);
    setIsGeneralInsuranceOpen(true);
  };

  const handleDeleteInsurance = async () => {
    try {
      await deleteInsuranceMutation(insuranceToDelete.id);
      await refetchUserQualificationsByType();
      dispatch(showToast({ message: "Insurance deleted successfully", type: "success" }));
      setInsuranceToDelete(null);
    } catch (error) {
      dispatch(showToast({ message: "Failed to delete insurance", type: "error" }));
    }
  };

  return (
    <section className="py-4 px-5 ">
      {insurances?.data?.map((insurance) => {
        const fileUrl = insurance?.url || insurance?.digitalAsset?.url;
        const fileName = insurance?.fileName || insurance?.digitalAsset?.fileName || "Attachment";
        return (
          <div
            className="mb-4 bg-light border border-gray-200 rounded-lg py-4 px-5 w-full"
            key={insurance.id}
          >
            <div className="flex justify-between items-start gap-3">
              <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-x-10 gap-y-2 min-w-0">
                <p className="font-normal">
                  Insurance Type:{" "}
                  <span className="font-300 ml-1">{insurance?.qualificationSubType}</span>
                </p>
                {fileUrl && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 min-w-0">
                    <p className="font-normal shrink-0">Attachment:</p>
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-[4px] py-1 pl-3 pr-4 cursor-pointer text-blue-500 hover:underline font-300 min-w-0 max-w-full"
                    >
                      <i className="fa-solid fa-paperclip shrink-0"></i>
                      <span className="break-all">{fileName}</span>
                    </a>
                  </div>
                )}
              </div>
              {
                <div className="flex items-center gap-3 shrink-0">
                  <i
                    className="fa-regular fa-trash-can text-red-600 cursor-pointer"
                    title="Delete"
                    onClick={() => setInsuranceToDelete(insurance)}
                  ></i>
                  <i
                    className="fa-light fa-pencil text-gray-900 cursor-pointer"
                    title="Edit"
                    onClick={() => handleEditInsurance(insurance)}
                  ></i>
                </div>
              }
            </div>
          </div>
        );
      })}

      {
        <div className="flex justify-end">
          <BaseButton
            color="primary"
            variant="outlined"
            className="py-[7px] px-[10px]"
            onClick={handleUploadClick}
          >
            Upload Policy
          </BaseButton>
        </div>
      }

      {isGeneralInsuranceOpen && (
        <AddGeneralInsuranceModal
          refetchUserQualificationsByType={refetchUserQualificationsByType}
          open={isGeneralInsuranceOpen}
          close={setIsGeneralInsuranceOpen}
          insuranceId={insuranceId}
          selectedInsurance={selectedInsurance}
          setSelectedInsurance={setSelectedInsurance}
        />
      )}

      {insuranceToDelete && (
        <BaseConfirmModal
          open={!!insuranceToDelete}
          onClose={() => setInsuranceToDelete(null)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete Insurance"
          description="Are you sure you want to delete this insurance policy? This action cannot be undone."
          isLoading={isDeleting}
          onConfirm={handleDeleteInsurance}
        />
      )}
    </section>
  );
};

export default Insurance;
